/**
 * GID-220: markup for the attached-file chips above the composer.
 *
 * Kept apart from taskpane.js so the per-state markup (uploading, processing,
 * ready, failed) can be read and tested without the whole pane. Clicks are
 * handled by one delegated listener in the pane, keyed on data-local-id, so a
 * re-render on every status change never leaves a stale handler behind.
 */

import { STATUS, isInFlight, stageLabel } from "./attachmentUploads.js";

function escapeText(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function chipIcon(entry) {
  if (isInFlight(entry)) return '<span class="chip-spinner" aria-hidden="true"></span>';
  if (entry.status === STATUS.FAILED) return '<span class="chip-icon" aria-hidden="true">⚠</span>';
  return '<span class="chip-icon" aria-hidden="true">📎</span>';
}

export function buildAttachedChipHtml(entry) {
  const status = entry.status || STATUS.READY;
  const name = escapeText(entry.filename || "file");
  const id = escapeText(entry.local_id);
  const label = escapeText(stageLabel(entry));
  const busy = isInFlight(entry);
  const removing = Boolean(entry.removing);

  // dir="auto" lets a Hebrew filename or server error lay itself out RTL
  // inside an otherwise LTR chip (and the reverse in an RTL pane).
  const statusText = label
    ? `<span class="chip-status" dir="auto" title="${label}">${label}</span>`
    : "";
  const retry =
    status === STATUS.FAILED
      ? `<button type="button" class="chip-retry" data-action="retry" data-local-id="${id}"` +
        ` aria-label="Retry ${name}" title="Retry"${removing ? " disabled" : ""}>↻</button>`
      : "";
  const remove =
    `<button type="button" class="chip-remove" data-action="remove" data-local-id="${id}"` +
    ` aria-label="Remove ${name}" title="Remove"${removing ? " disabled" : ""}>×</button>`;

  return (
    `<span class="attached-chip is-${escapeText(status)}" data-local-id="${id}"` +
    `${busy ? ' aria-busy="true"' : ""}>` +
    chipIcon(entry) +
    `<span class="chip-name" dir="auto" title="${name}">${name}</span>` +
    statusText +
    retry +
    remove +
    "</span>"
  );
}

export function buildAttachedChipsHtml(entries) {
  return (entries || []).map(buildAttachedChipHtml).join("");
}
